<template>
  <div class="avu" :class="{ 'avu--disabled': disabled }">
    <span v-if="label" class="avu__label">{{ label }}</span>

    <!-- frame = circle + corner buttons; the circle clips the picture, the
         buttons sit on its rim outside the clipping -->
    <div class="avu__frame">
      <!-- The round picture IS the upload target: click, or drop a file on it. -->
      <div
          class="avu__circle"
          :class="{ 'avu__circle--drag': dragging, 'avu__circle--empty': !value }"
          role="button"
          :tabindex="disabled ? -1 : 0"
          :title="disabled ? null : (value ? changeLabel : uploadLabel)"
          @click="openPicker"
          @keydown.enter.prevent="openPicker"
          @keydown.space.prevent="openPicker"
          @dragover.prevent="onDragOver"
          @dragleave="dragging = false"
          @drop.prevent="onDrop"
      >
        <img v-if="value" :src="value" alt="">
        <md-icon v-else class="avu__placeholder">person</md-icon>
        <div v-if="dragging" class="avu__overlay">
          <md-icon>file_upload</md-icon>
          <span>{{ dropLabel }}</span>
        </div>
        <div v-if="busy" class="avu__busy"><span class="avu__spinner"></span></div>
      </div>

      <!-- bottom-right: upload / change; bottom-left: remove (only with a picture) -->
      <button
          v-if="!disabled"
          type="button"
          class="avu__fab avu__fab--right"
          :title="value ? changeLabel : uploadLabel"
          :aria-label="value ? changeLabel : uploadLabel"
          :disabled="busy"
          @click.stop="openPicker"
      >
        <md-icon>photo_camera</md-icon>
      </button>
      <button
          v-if="!disabled && value"
          type="button"
          class="avu__fab avu__fab--left avu__fab--danger"
          :title="removeLabel"
          :aria-label="removeLabel"
          :disabled="busy"
          @click.stop="remove"
      >
        <md-icon>delete</md-icon>
      </button>
    </div>

    <span v-if="hint && !disabled" class="avu__hint">{{ hint }}</span>

    <input ref="file" class="avu__input" type="file" :accept="ACCEPT" :disabled="disabled" @change="onPick">
  </div>
</template>

<script>
/**
 * Round profile-picture uploader (v-model = data URL or null).
 *
 * One circle shows the current picture (or a person icon) and doubles as the
 * click / drop target; two round icon buttons on its bottom rim upload/change
 * (right) and remove (left, only with a picture).
 * The picked file is validated (JPG/PNG, <= maxBytes), scaled down in a
 * canvas to `maxSide` px on its longer edge and emitted as a data URL through
 * `input`. Browser-side scaling is deliberate: it keeps the stored base64
 * small and spares the API a full-size upload (same as the previous
 * vue-image-upload-resize flow, just without its own input markup).
 * Problems are emitted as `error` with a locale KEY (user_pic_invalid_type /
 * user_pic_too_big / user_pic_unreadable); the parent shows the message.
 */
const ACCEPT = 'image/jpeg,image/png';
const TYPES = ['image/jpeg', 'image/png'];

export default {
  name: 'AvatarUpload',
  props: {
    value: { type: String, default: null },
    disabled: { type: Boolean, default: false },
    label: { type: String, default: '' },
    uploadLabel: { type: String, default: 'Upload picture' },
    changeLabel: { type: String, default: 'Change picture' },
    removeLabel: { type: String, default: 'Remove picture' },
    dropLabel: { type: String, default: 'Drop to upload' },
    hint: { type: String, default: '' },
    maxBytes: { type: Number, default: 5 * 1024 * 1024 },
    maxSide: { type: Number, default: 400 }
  },
  data() {
    return { ACCEPT, dragging: false, busy: false };
  },
  methods: {
    openPicker() {
      if (this.disabled || this.busy) return;
      this.$refs.file.click();
    },
    remove() {
      if (this.disabled || this.busy) return;
      this.$emit('input', null);
    },
    onDragOver() {
      if (this.disabled || this.busy) return;
      this.dragging = true;
    },
    onDrop(event) {
      this.dragging = false;
      if (this.disabled || this.busy) return;
      const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
      if (file) this.handleFile(file);
    },
    onPick(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = ''; // same file may be picked again after a remove
      if (file) this.handleFile(file);
    },
    async handleFile(file) {
      if (!TYPES.includes(file.type)) { this.$emit('error', 'user_pic_invalid_type'); return; }
      if (file.size > this.maxBytes) { this.$emit('error', 'user_pic_too_big'); return; }
      this.busy = true;
      try {
        const dataUrl = await this.scale(file);
        this.$emit('input', dataUrl);
      } catch (e) {
        this.$emit('error', 'user_pic_unreadable');
      } finally {
        this.busy = false;
      }
    },
    /** Scales the picture to `maxSide` on its longer edge; PNG keeps PNG (transparency), else JPEG. */
    scale(file) {
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
          URL.revokeObjectURL(url);
          const ratio = Math.min(1, this.maxSide / Math.max(img.naturalWidth, img.naturalHeight));
          const w = Math.max(1, Math.round(img.naturalWidth * ratio));
          const h = Math.max(1, Math.round(img.naturalHeight * ratio));
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          canvas.getContext('2d').drawImage(img, 0, 0, w, h);
          const png = file.type === 'image/png';
          resolve(canvas.toDataURL(png ? 'image/png' : 'image/jpeg', png ? undefined : 0.85));
        };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('unreadable image')); };
        img.src = url;
      });
    }
  }
};
</script>

<style scoped>
.avu { display: flex; flex-direction: column; gap: 0.6rem; width: var(--avu-size, 220px); }
.avu__label { font-size: 0.78rem; font-weight: 600; color: #555; white-space: nowrap; }

.avu__frame { position: relative; width: var(--avu-size, 220px); height: var(--avu-size, 220px); }

.avu__circle {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;
  background: #f5f6fa;
  border: 1px solid #d5d5d5;
  cursor: pointer;
  outline: none;
  transition: box-shadow .15s ease, border-color .15s ease;
}
.avu__circle:hover { box-shadow: 0 2px 10px rgba(20, 30, 45, 0.14); }
.avu__circle:focus-visible { box-shadow: 0 0 0 3px rgba(52, 56, 65, 0.25); }
.avu__circle--empty { border-style: dashed; }
.avu__circle--drag { border-color: #343841; box-shadow: 0 0 0 3px rgba(52, 56, 65, 0.18); }
.avu--disabled .avu__circle { cursor: default; box-shadow: none; }

.avu__circle img { display: block; width: 100%; height: 100%; object-fit: cover; }
.avu__placeholder {
  position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
  font-size: 112px !important; color: #b8bcc4 !important;
}

/* drag overlay */
.avu__overlay {
  position: absolute; inset: 0;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px;
  background: rgba(32, 36, 44, 0.55);
  color: #fff; font-size: 0.74rem; font-weight: 600; text-align: center; padding: 0 14px;
  pointer-events: none;
}
.avu__overlay .md-icon { color: #fff !important; }

.avu__busy {
  position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
  background: rgba(255, 255, 255, 0.6);
}
.avu__spinner {
  width: 32px; height: 32px; border-radius: 50%;
  border: 3px solid #e3e6ea; border-top-color: #343841;
  animation: avu-spin 0.8s linear infinite;
}
@keyframes avu-spin { to { transform: rotate(360deg); } }

/* round icon buttons on the bottom rim of the circle */
.avu__fab {
  position: absolute;
  bottom: 6px;
  width: 44px; height: 44px;
  padding: 0;
  border: 2px solid #fff;
  border-radius: 50%;
  background: #343841;
  color: #fff;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 2px 8px rgba(20, 30, 45, 0.25);
  transition: background .15s ease, transform .15s ease;
}
.avu__fab .md-icon { color: #fff !important; font-size: 22px !important; }
.avu__fab:not(:disabled):hover { background: #2a2e36; transform: scale(1.06); }
.avu__fab:disabled { opacity: 0.55; cursor: not-allowed; }
.avu__fab--right { right: 6px; }
.avu__fab--left { left: 6px; }
.avu__fab--danger { background: #e0524c; }
.avu__fab--danger:not(:disabled):hover { background: #cf463f; }

.avu__hint { font-size: 0.72rem; color: #8b929b; line-height: 1.3; }

.avu__input { display: none; }
</style>
